/**
 * Verification tab — auditable execution history with latest-first emphasis.
 *
 * Each click that triggers verification produces an `EpiArtifactExecution`
 * row. The newest is rendered expanded with four epi-framed sections —
 * Coverage, Distribution, Concept usage, Technical checks — laid out as
 * structured typography (no chip soup). Prior executions appear collapsed
 * below as a thin audit log; click expands the same four-section view.
 *
 * Chips are reserved for things the epi can drill into (dataset opens
 * the release-details modal). Status, counts, percentiles, lint findings
 * are typography — they're informational, not navigable.
 *
 * The trail is a regulatory artefact: "every time this analytical variable touched real
 * patient data, who did it, when, against what dataset". Cached re-runs
 * are still recorded; the inline "cached" suffix distinguishes them.
 */

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import { ArtifactTabCard } from './ArtifactTabCard';
import { useDatasetReleaseDetailsQuery } from '../../api/useDatasetReleaseDetailsQuery';
import {
  useVariableDetailByName,
  useVariableDetailByRef,
  type AvDetail,
} from '../../api/useVariableDetailByName';
import type {
  EpiArtifact,
  EpiArtifactExecution,
  EpiExecutionCoverage,
  EpiExecutionDistribution,
} from '../../types/epi-chat-types';

const formatPercent = (numerator: number, denominator: number): string => {
  if (denominator === 0) return '0.0%';
  if (!denominator) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
};

const formatNumber = (n: number | null | undefined): string =>
  n == null ? '—' : n.toLocaleString();

interface StatusGlyphProps {
  status: EpiArtifactExecution['status'];
  /**
   * When true, render only the icon — no text label. Used in collapsed
   * history rows where the row's timestamp + actor + dataset already
   * carry the context, and a redundant "passed" label adds visual noise.
   * The expanded view always renders the full glyph (icon + label) so
   * the status colour-meaning is unmissable.
   */
  iconOnly?: boolean;
}

/**
 * Inline status indicator — icon + (optional) label, theme-coloured.
 * Replaces the outlined chip variant; status is informational so a
 * dense icon-and-text pairing reads cleaner than a pill.
 */
const StatusGlyph = ({ status, iconOnly = false }: StatusGlyphProps) => {
  const { formatMessage } = useIntl();
  const [icon, label, color] = (() => {
    switch (status) {
      case 'pending':
        return [
          <CircularProgress key="i" size={12} thickness={5} sx={{ color: 'primary.main' }} />,
          formatMessage({ id: 'epiChat.verification.status.running' }),
          'primary.main',
        ] as const;
      case 'passed':
        return [
          <CheckCircleOutlineIcon key="i" fontSize="small" sx={{ color: 'success.main' }} />,
          formatMessage({ id: 'epiChat.verification.status.passed' }),
          'success.main',
        ] as const;
      case 'failed':
        return [
          <ErrorOutlineIcon key="i" fontSize="small" sx={{ color: 'error.main' }} />,
          formatMessage({ id: 'epiChat.verification.status.failed' }),
          'error.main',
        ] as const;
      case 'error':
        return [
          <ErrorOutlineIcon key="i" fontSize="small" sx={{ color: 'error.main' }} />,
          formatMessage({ id: 'epiChat.verification.status.error' }),
          'error.main',
        ] as const;
      // OR-5983: executed cleanly but produced no qualifying subjects (0%
      // retained). A non-pass, but distinct from a hard failure/error.
      case 'empty':
        return [
          <ErrorOutlineIcon key="i" fontSize="small" sx={{ color: 'warning.main' }} />,
          'no subjects',
          'warning.main',
        ] as const;
    }
  })();
  if (iconOnly) {
    // aria-label keeps the status announceable to screen readers even
    // though the visible text is hidden.
    return (
      <Box
        component="span"
        aria-label={label}
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {icon}
      </Box>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {icon}
      {/* TODO: no VDS token for letterSpacing '0.04em' — needs designer input */}
      <Typography variant="caption" sx={{ color, fontWeight: 600, letterSpacing: '0.04em' }}>
        {label}
      </Typography>
    </Stack>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={4}>
    {/* TODO: no VDS token for lineHeight '24px' — needs designer input */}
    <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: '24px' }}>
      {title}
    </Typography>
    {children}
  </Stack>
);

/**
 * Label–value row matching the Figma measured-population and distribution
 * sections: secondary label on the left, bold numeric value right-aligned.
 */
const StatRow = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={6}>
    <Typography
      variant="body1"
      color="text.secondary"
      component="span"
      sx={{ flex: 1, lineHeight: 1.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={600}
      sx={{
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        flexShrink: 0,
        lineHeight: 1.5,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

/** Bordered card wrapper for stat tables (measured population, distribution). */
const StatCard = ({ children }: { children: ReactNode }) => (
  <Paper elevation={1} sx={{ padding: 4, border: '1px solid', borderColor: 'divider' }}>
    <Stack spacing={3}>{children}</Stack>
  </Paper>
);

// Formats ISO date "YYYY-MM-DD" → "MM-DD-YYYY" for the fixed index date label.

const formatStudyDate = (dateStr: string): string => {
  // Force UTC parse to avoid timezone-shifted day-off-by-one.
  const d = new Date(`${dateStr}T00:00:00Z`);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Fetches and renders the index variable name + non-null count with percentage.
 * Falls back to slug display if the library lookup fails (e.g. draft-only AV).
 * Shows a spinner while the AV info is loading.
 */
const AnchorAvRow = ({
  name,
  anchorId,
  totalPatients,
  computedPatients,
  onAvNameClick,
}: {
  name: string;
  anchorId?: string;
  totalPatients?: number;
  computedPatients?: number;
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void;
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  // OR-6433: resolve the anchor by its durable id when the result recorded one. The
  // alias is what the anchor was called at verification time, so a by-alias lookup
  // stops resolving the moment the anchor is renamed — leaving this row showing the
  // raw alias with no count and no click-through. Both hooks are called every render
  // (hook rules) and each is disabled when the other is in use, so only one fetches.
  // OR-6234 AC 6: the anchor may since have been deleted; this row still shows it.
  // ``useVariableDetailByRef`` always includes deleted rows; the by-name read opts in.
  const byRef = useVariableDetailByRef(anchorId ?? null, projectId, null);
  const byName = useVariableDetailByName(anchorId ? null : name, projectId, {
    includeDeleted: true,
  });
  const { data, isError, isLoading } = anchorId ? byRef : byName;
  const info: AvDetail | undefined = data?.data;
  const { formatMessage } = useIntl();

  const displayName = !isError && info ? info.dataElement : name;

  const verificationResults = Array.isArray(info?.manifest?.['verification_results'])
    ? (info.manifest['verification_results'] as unknown[])
    : [];
  const latestCoverage =
    verificationResults.length > 0 &&
    typeof verificationResults[0] === 'object' &&
    verificationResults[0] !== null
      ? (verificationResults[0] as Record<string, unknown>)['coverage']
      : null;
  const nonNullPatients =
    typeof latestCoverage === 'object' &&
    latestCoverage !== null &&
    typeof (latestCoverage as Record<string, unknown>)['patients_with_value'] === 'number'
      ? ((latestCoverage as Record<string, unknown>)['patients_with_value'] as number)
      : null;

  // Use the count from the current execution's coverage when available;
  // fall back to the anchor AV's own latest manifest (different dataset context).
  const resolvedPatients = computedPatients ?? nonNullPatients;

  const nonNullValue = (() => {
    if (computedPatients == null && isLoading) {
      return <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary' }} />;
    }
    if (resolvedPatients != null && totalPatients != null) {
      return formatMessage(
        { id: 'epiChat.verification.value.patientsWithPercent' },
        {
          count: formatNumber(resolvedPatients),
          percent: formatPercent(resolvedPatients, totalPatients),
        }
      );
    }
    if (resolvedPatients != null) {
      return formatMessage(
        { id: 'epiChat.verification.value.patients' },
        { count: formatNumber(resolvedPatients) }
      );
    }
    return '—';
  })();

  const onAvNameClickHandler = () => {
    if (onAvNameClick && info?.analyticalVariableId) {
      // The preview this opens must resolve a deleted anchor too (OR-6234 AC 6).
      onAvNameClick(info.analyticalVariableId, { includeDeleted: true });
    }
  };

  const avNameClickable = !!onAvNameClick && !!info?.analyticalVariableId;

  return (
    <StatRow
      label={
        <>
          {formatMessage({ id: 'epiChat.verification.stat.anchor' })}
          {avNameClickable ? (
            <Box
              component="button"
              onClick={onAvNameClickHandler}
              sx={{
                background: 'none',
                border: 0,
                padding: 0,
                color: 'primary.main',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                textDecoration: 'underline',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              {displayName}
            </Box>
          ) : (
            <Box component="span">{displayName}</Box>
          )}
        </>
      }
      value={nonNullValue}
    />
  );
};

/**
 * "Measured population" section — replaces the old Coverage section.
 * Shows full dataset size, anchor population (when anchored), and study period.
 * Layout matches the Figma: label left / bold value right.
 */
const MeasuredPopulationSection = ({
  coverage,
  studyStart,
  studyEnd,
  indexAvSlug,
  indexAvId,
  indexCalendarDate,
  onAvNameClick,
}: {
  coverage: EpiExecutionCoverage;
  studyStart?: string;
  studyEnd?: string;
  indexAvSlug?: string;
  indexAvId?: string;
  indexCalendarDate?: string;
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void;
}) => {
  // Anchor population = patients who have a non-null anchor date.
  const studyPeriodStr = (() => {
    if (!studyStart && !studyEnd) return null;
    const s = studyStart ? formatStudyDate(studyStart) : null;
    const e = studyEnd ? formatStudyDate(studyEnd) : null;
    if (s && e) return `${s} — ${e}`;
    return s ?? e;
  })();

  const { formatMessage } = useIntl();
  return (
    <Section title={formatMessage({ id: 'epiChat.verification.section.measuredPopulation' })}>
      <StatCard>
        <StatRow
          label={formatMessage({ id: 'epiChat.verification.stat.fullDataset' })}
          value={formatMessage(
            { id: 'epiChat.verification.value.patients' },
            { count: formatNumber(coverage.patientsInCohort) }
          )}
        />
        {indexAvSlug && (
          <AnchorAvRow
            name={indexAvSlug}
            anchorId={indexAvId}
            totalPatients={coverage.patientsInCohort}
            computedPatients={coverage.patientsWithAnchor}
            onAvNameClick={onAvNameClick}
          />
        )}
        {indexCalendarDate && !indexAvSlug && (
          <StatRow
            label={formatMessage({ id: 'epiChat.verification.stat.index' })}
            value={formatStudyDate(indexCalendarDate)}
          />
        )}
        {studyPeriodStr && (
          <StatRow
            label={formatMessage({ id: 'epiChat.verification.stat.studyPeriod' })}
            value={studyPeriodStr}
          />
        )}
      </StatCard>
    </Section>
  );
};

/**
 * Distribution section — format-aware layout matching the Figma spec and
 * Confluence doc. Each output type shows its own set of stats; null and
 * total-non-null are derived from coverage when available.
 */
const DistributionSection = ({
  distribution,
  coverage,
}: {
  distribution: EpiExecutionDistribution;
  coverage?: EpiExecutionCoverage;
}) => {
  // Null / total-non-null derived from coverage (applies to numeric, date, string).
  const totalNonNull = coverage?.patientsWithValue ?? null;
  const populationSize = coverage?.patientsWithAnchor ?? coverage?.patientsInCohort ?? null;
  const nullFromCoverage =
    coverage != null && populationSize != null ? populationSize - coverage.patientsWithValue : null;

  const { formatMessage } = useIntl();
  const nullLabel = formatMessage({ id: 'epiChat.verification.stat.null' });
  const totalNonNullLabel = formatMessage({ id: 'epiChat.verification.stat.totalNonNull' });
  return (
    <Section title={formatMessage({ id: 'epiChat.verification.section.distribution' })}>
      <StatCard>
        {distribution.kind === 'boolean' &&
          (() => {
            const total =
              populationSize ??
              distribution.truePatients +
                distribution.falsePatients +
                (distribution.nullPatients ?? 0);
            const boolNull =
              distribution.nullPatients ??
              (populationSize != null
                ? populationSize - distribution.truePatients - distribution.falsePatients
                : null);
            const boolNonNull = distribution.truePatients + distribution.falsePatients;
            return (
              <Stack spacing={3}>
                <StatRow
                  label={formatMessage({ id: 'epiChat.verification.stat.true' })}
                  value={`${formatNumber(distribution.truePatients)} (${formatPercent(
                    distribution.truePatients,
                    total
                  )})`}
                />
                <StatRow
                  label={formatMessage({ id: 'epiChat.verification.stat.false' })}
                  value={`${formatNumber(distribution.falsePatients)} (${formatPercent(
                    distribution.falsePatients,
                    total
                  )})`}
                />
                {boolNull != null && (
                  <StatRow
                    label={nullLabel}
                    value={`${formatNumber(boolNull)} (${formatPercent(boolNull, total)})`}
                  />
                )}
                <StatRow
                  label={totalNonNullLabel}
                  value={`${formatNumber(boolNonNull)} (${formatPercent(boolNonNull, total)})`}
                />
              </Stack>
            );
          })()}

        {distribution.kind === 'numeric' && (
          <Stack spacing={3}>
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.meanSD' })}
              value={
                distribution.stdDev != null
                  ? `${formatNumber(distribution.mean)} (${formatNumber(distribution.stdDev)})`
                  : formatNumber(distribution.mean)
              }
            />
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.medianIQR' })}
              value={`${formatNumber(distribution.median)} [${formatNumber(
                distribution.p25
              )}, ${formatNumber(distribution.p75)}]`}
            />
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.min' })}
              value={formatNumber(distribution.min)}
            />
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.max' })}
              value={formatNumber(distribution.max)}
            />
            {nullFromCoverage != null && populationSize != null && (
              <StatRow
                label={nullLabel}
                value={`${formatNumber(nullFromCoverage)} (${formatPercent(
                  nullFromCoverage,
                  populationSize
                )})`}
              />
            )}
            {totalNonNull != null && populationSize != null && (
              <StatRow
                label={totalNonNullLabel}
                value={`${formatNumber(totalNonNull)} (${formatPercent(
                  totalNonNull,
                  populationSize
                )})`}
              />
            )}
          </Stack>
        )}

        {distribution.kind === 'date' && (
          <Stack spacing={3}>
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.earliest' })}
              value={distribution.earliest}
            />
            <StatRow
              label={formatMessage({ id: 'epiChat.verification.stat.latest' })}
              value={distribution.latest}
            />
            {distribution.medianDate && (
              <StatRow
                label={formatMessage({ id: 'epiChat.verification.stat.medianDate' })}
                value={distribution.medianDate}
              />
            )}
            {nullFromCoverage != null && populationSize != null && (
              <StatRow
                label={nullLabel}
                value={`${formatNumber(nullFromCoverage)} (${formatPercent(
                  nullFromCoverage,
                  populationSize
                )})`}
              />
            )}
            {totalNonNull != null && populationSize != null && (
              <StatRow
                label={totalNonNullLabel}
                value={`${formatNumber(totalNonNull)} (${formatPercent(
                  totalNonNull,
                  populationSize
                )})`}
              />
            )}
          </Stack>
        )}

        {distribution.kind === 'string' && (
          <Stack spacing={3}>
            {(distribution.topValues ?? []).slice(0, 20).map((entry) => (
              <StatRow
                key={entry.value}
                label={entry.value}
                value={`${formatNumber(entry.count)} (${formatPercent(
                  entry.count,
                  populationSize ?? 0
                )})`}
              />
            ))}
            {distribution.otherCount != null && distribution.otherCount > 0 && (
              <StatRow
                label={
                  distribution.otherValueCount != null
                    ? formatMessage(
                        { id: 'epiChat.verification.stat.otherCategoriesWithCount' },
                        { count: distribution.otherValueCount }
                      )
                    : formatMessage({ id: 'epiChat.verification.stat.otherCategories' })
                }
                value={
                  distribution.otherPercentage != null
                    ? `${formatNumber(distribution.otherCount)} (${formatPercent(
                        distribution.otherCount,
                        populationSize ?? 0
                      )})`
                    : formatNumber(distribution.otherCount)
                }
              />
            )}
            {nullFromCoverage != null && populationSize != null && (
              <StatRow
                label={nullLabel}
                value={`${formatNumber(nullFromCoverage)} (${formatPercent(
                  nullFromCoverage,
                  populationSize
                )})`}
              />
            )}
            {totalNonNull != null && populationSize != null && (
              <StatRow
                label={totalNonNullLabel}
                value={`${formatNumber(totalNonNull)} (${formatPercent(
                  totalNonNull,
                  populationSize
                )})`}
              />
            )}
          </Stack>
        )}
      </StatCard>
    </Section>
  );
};

const formatGeneratedTime = (isoStr: string): string => {
  const d = new Date(isoStr);
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} | ${time}`;
};

/** Run details section — dataset and generation timestamp in a stat card. */
const RunDetailsSection = ({ execution }: { execution: EpiArtifactExecution }) => {
  const { formatMessage } = useIntl();
  const datasetReleaseLookupEnabled =
    Boolean(execution.datasetReleaseId) && !execution.datasetReleaseDisplayName;
  const { data: datasetReleaseDetails } = useDatasetReleaseDetailsQuery(
    execution.datasetReleaseId,
    datasetReleaseLookupEnabled
  );
  const dataset =
    datasetReleaseDetails?.data.releaseName ??
    execution.datasetReleaseDisplayName ??
    execution.datasetReleaseId;
  const generated = formatGeneratedTime(execution.completedAt ?? execution.startedAt);
  return (
    <Section title={formatMessage({ id: 'epiChat.verification.section.runDetails' })}>
      <StatCard>
        <StatRow
          label={formatMessage({ id: 'epiChat.verification.stat.dataset' })}
          value={dataset}
        />
        <StatRow
          label={formatMessage({ id: 'epiChat.verification.stat.generated' })}
          value={generated}
        />
      </StatCard>
    </Section>
  );
};

const ExecutionMetaLine = ({ execution }: { execution: EpiArtifactExecution }) => {
  const { formatMessage } = useIntl();
  const timestamp = execution.completedAt
    ? new Date(execution.completedAt).toLocaleString()
    : new Date(execution.startedAt).toLocaleString();
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      sx={{ rowGap: 0.25, columnGap: 0.5 }}
    >
      <Typography variant="caption" color="text.secondary">
        {timestamp}
        {execution.userName ? ` · ${execution.userName}` : ''}
      </Typography>
      {execution.computeMode === 'cached' && (
        <Typography variant="caption" color="text.secondary">
          {formatMessage({ id: 'epiChat.verification.badge.cached' })}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Error state for failed/error executions.
 * `compact` = inline left-aligned row used in history rows.
 * Default = centered full-height view used in the latest execution panel.
 */
const ExecutionErrorView = ({
  execution,
  compact = false,
}: {
  execution: EpiArtifactExecution;
  compact?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const errorMessage = useMemo(() => {
    if (
      execution.technicalChecks?.lintFindings &&
      execution.technicalChecks?.lintFindings?.length > 0
    ) {
      const messages = execution.technicalChecks?.lintFindings
        ?.filter((finding) => finding.severity === 'error')
        .map((finding) => finding.message);
      if (messages && messages.length > 0) {
        return messages.join('\n');
      }
    }
    return formatMessage({ id: 'epiChat.verification.error.subtitle' });
  }, [execution.status, formatMessage, execution.technicalChecks?.lintFindings]);

  if (compact) {
    return (
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatMessage({ id: 'epiChat.verification.failed.title' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {errorMessage}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        minHeight: 348,
      }}
    >
      <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ErrorIcon sx={{ color: 'error.main', fontSize: fontSize[24] }} />
          <Typography variant="h5">
            {formatMessage({ id: 'epiChat.verification.failed.title' })}
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          {errorMessage}
        </Typography>
      </Stack>
    </Box>
  );
};

const ExecutionFullView = ({
  execution,
  onAvNameClick,
}: {
  execution: EpiArtifactExecution;
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void;
}) => {
  return (
    <Stack spacing={7}>
      {execution.coverage && (
        <MeasuredPopulationSection
          coverage={execution.coverage}
          studyStart={execution.studyStart}
          studyEnd={execution.studyEnd}
          indexAvSlug={execution.indexAvSlug}
          indexAvId={execution.indexAvId}
          indexCalendarDate={execution.indexCalendarDate}
          onAvNameClick={onAvNameClick}
        />
      )}
      {execution.distribution && (
        <DistributionSection distribution={execution.distribution} coverage={execution.coverage} />
      )}
      <RunDetailsSection execution={execution} />
      {/* Pending executions don't have section data yet — show the live
          log tail instead so the epi can watch progress. */}
      {execution.status === 'pending' && execution.logs && (
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: (theme) => theme.typography.caption.fontSize,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'text.secondary',
          }}
          data-testid={`execution-${execution.executionId}-logs`}
        >
          {execution.logs}
        </Box>
      )}
    </Stack>
  );
};

const HistoryRow = ({
  execution,
  onAvNameClick,
}: {
  execution: EpiArtifactExecution;
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box data-testid={`execution-row-${execution.executionId}`}>
      <Box
        component="button"
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          background: 'none',
          border: 0,
          textAlign: 'left',
          paddingX: 0,
          paddingY: 0.5,
          cursor: 'pointer',
          gap: 1,
          '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
        }}
      >
        {/* Icon-only in the collapsed row — the timestamp + actor +
            dataset already carry context, and a redundant "passed"
            label adds noise. The expanded view shows the full glyph. */}
        <StatusGlyph status={execution.status} iconOnly />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ExecutionMetaLine execution={execution} />
        </Box>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      {expanded && (
        <Box sx={{ paddingLeft: 1, paddingY: 1 }}>
          {execution.status === 'failed' || execution.status === 'error' ? (
            <ExecutionErrorView execution={execution} compact />
          ) : (
            <ExecutionFullView execution={execution} onAvNameClick={onAvNameClick} />
          )}
        </Box>
      )}
    </Box>
  );
};

export const VerificationTab = ({
  artifact,
  onAvNameClick,
}: {
  artifact: EpiArtifact;
  onJumpToSqlLine?: (line: number) => void;
  onAvNameClick?: (id: string, options?: { includeDeleted?: boolean }) => void;
}) => {
  const { formatMessage } = useIntl();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const executions = artifact.executions ?? [];
  if (executions.length === 0) {
    return (
      <ArtifactTabCard data-testid="artifact-verification-tab">
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack spacing={2} sx={{ textAlign: 'center' }}>
            <Typography variant="h5">
              {formatMessage({ id: 'epiChat.verification.empty' })}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formatMessage({ id: 'epiChat.verification.emptySubtitle' })}
            </Typography>
          </Stack>
        </Box>
      </ArtifactTabCard>
    );
  }
  const [latest, ...history] = executions;
  const allFailed = executions.every((e) => e.status === 'failed' || e.status === 'error');
  return (
    <ArtifactTabCard data-testid="artifact-verification-tab">
      <Stack sx={{ gap: 1.5 }}>
        {!allFailed && (
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ fontSize: fontSize[16], mb: 2, maxWidth: '900px' }}
          >
            {formatMessage({ id: 'epiChat.verification.isolationDisclaimer' })}
          </Typography>
        )}
        {latest.status === 'failed' || latest.status === 'error' ? (
          <ExecutionErrorView execution={latest} />
        ) : (
          <ExecutionFullView execution={latest} onAvNameClick={onAvNameClick} />
        )}
        {history.length > 0 && (
          <Box>
            {/* Divider above the History section was redundant — the
              overline header alone is enough demarcation, and the extra
              line broke up the vertical rhythm. */}
            <Stack
              component="button"
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setHistoryExpanded((p) => !p)}
              sx={{
                background: 'none',
                border: 0,
                padding: 0,
                width: '100%',
                cursor: 'pointer',
                textAlign: 'left',
                marginBottom: historyExpanded ? 0.5 : 0,
              }}
              data-testid="execution-history-toggle"
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ flex: 1, fontWeight: 700, lineHeight: 1.2 }}
              >
                {formatMessage(
                  { id: 'epiChat.verification.historyToggle' },
                  { count: history.length }
                )}
              </Typography>
              <IconButton size="small" component="span">
                {historyExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>
            {historyExpanded && (
              <Stack divider={<Divider flexItem />} spacing={0.25}>
                {history.map((execution) => (
                  <HistoryRow
                    key={execution.executionId}
                    execution={execution}
                    onAvNameClick={onAvNameClick}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </ArtifactTabCard>
  );
};
